import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { TASK_TRANSITIONS } from '@/shared/utils/taskTransitions';
import type { TaskAssignee, TaskTag } from '@/shared/types/taskQueue.types';
import type { TaskDetailDto } from '../../schemas/tasks.schemas';
import type { TaskLifecycleAction, TaskStatus } from '@/shared/types/taskQueue.types';
import {
  addTaskComment,
  getApiErrorMessage,
  getTask,
  listTaskAssignees,
  listTaskTags,
  taskLifecycle,
  updateTask,
} from '../../services/tasksAPI';
import { formatDateTimeShort } from '../../utils/formatCreatedAt';
import { getSlackSourceLinks } from '../../utils/slackLink';
import TaskDetailNavbar from './TaskDetailNavbar';
import TaskMarkdown from './TaskMarkdown';
import { taskStatusLabel } from './taskStatusLabel';
import './TaskQueue.css';

const ASSIGNEE_MUTABLE_STATUSES: readonly TaskStatus[] = ['available', 'released', 'assigned'];

function buildWorkerAssigneeOptions(
  detail: TaskDetailDto,
  currentUserId: string | undefined,
  currentUserName: string,
): TaskAssignee[] {
  const options: TaskAssignee[] = [];
  if (currentUserId) {
    options.push({ id: currentUserId, name: currentUserName });
  }
  if (
    detail.assigneeId &&
    detail.assigneeId !== currentUserId &&
    !options.some((o) => o.id === detail.assigneeId)
  ) {
    options.push({
      id: detail.assigneeId,
      name: detail.assigneeName ?? 'Assignee',
    });
  }
  return options;
}

const TaskDetailPage: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const { id: taskId } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<TaskDetailDto | null>(null);
  const [adminAssignees, setAdminAssignees] = useState<TaskAssignee[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [tagCatalog, setTagCatalog] = useState<TaskTag[]>([]);
  const [loading, setLoading] = useState(true);

  const currentUserId = user?.userId;
  const currentUserName = user?.name ?? 'Me';

  usePageTitle(detail?.title ?? 'Task');

  const loadDetail = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    setMessage('');
    try {
      const t = await getTask(id);
      setDetail(t);
    } catch (err: unknown) {
      setDetail(null);
      setLoadError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!taskId) {
      setLoadError('Task not found');
      setLoading(false);
      return;
    }
    void loadDetail(taskId);
  }, [taskId, loadDetail]);

  useEffect(() => {
    if (!isAdmin) return;
    void listTaskAssignees()
      .then(setAdminAssignees)
      .catch(() => undefined);
  }, [isAdmin]);

  useEffect(() => {
    void listTaskTags()
      .then(setTagCatalog)
      .catch(() => undefined);
  }, []);

  const canEditTaskType = useMemo((): boolean => {
    if (!detail || !currentUserId) return false;
    return isAdmin || detail.createdBy === currentUserId;
  }, [detail, currentUserId, isAdmin]);

  const activeTypeTags = useMemo(
    () => tagCatalog.filter((t) => !t.retired),
    [tagCatalog],
  );

  const assigneeOptions = useMemo((): TaskAssignee[] => {
    let options: TaskAssignee[];
    if (isAdmin) {
      options = adminAssignees;
    } else if (!detail) {
      return [];
    } else {
      options = buildWorkerAssigneeOptions(detail, currentUserId, currentUserName);
    }
    if (
      detail?.assigneeId &&
      !options.some((o) => o.id === detail.assigneeId)
    ) {
      options = [
        ...options,
        {
          id: detail.assigneeId,
          name: detail.assigneeName ?? 'Assignee',
        },
      ];
    }
    return options;
  }, [isAdmin, adminAssignees, detail, currentUserId, currentUserName]);

  const assigneeSelectEnabled = useMemo((): boolean => {
    if (!detail || assigning || actionBusy) return false;
    if (!(ASSIGNEE_MUTABLE_STATUSES as readonly string[]).includes(detail.status)) return false;
    if (isAdmin) return true;
    if (!detail.assigneeId) return true;
    return detail.assigneeId === currentUserId;
  }, [detail, assigning, actionBusy, isAdmin, currentUserId]);

  const visibleActions = useMemo(() => {
    if (!detail) return [];
    return TASK_TRANSITIONS.filter(
      (t) =>
        t.action !== 'reassign' &&
        t.action !== 'assign' &&
        (t.from as readonly string[]).includes(detail.status),
    );
  }, [detail]);

  const runAction = async (action: TaskLifecycleAction): Promise<void> => {
    if (!detail || actionBusy || assigning) return;
    setActionBusy(true);
    setMessage('');
    try {
      const updated = await taskLifecycle(detail.id, action);
      setDetail(updated);
      await loadDetail(updated.id);
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setActionBusy(false);
    }
  };

  const onAssigneeChange = async (nextAssigneeId: string): Promise<void> => {
    if (!detail || !assigneeSelectEnabled) return;
    const previousAssigneeId = detail.assigneeId ?? '';
    if (nextAssigneeId === previousAssigneeId) return;

    setAssigning(true);
    setMessage('');
    try {
      if (nextAssigneeId === '') {
        if (detail.status !== 'assigned') {
          return;
        }
        const updated = await taskLifecycle(detail.id, 'release');
        setDetail(updated);
      } else if (
        nextAssigneeId === currentUserId &&
        (detail.status === 'available' || detail.status === 'released')
      ) {
        const updated = await taskLifecycle(detail.id, 'assign');
        setDetail(updated);
      } else {
        const updated = await taskLifecycle(detail.id, 'reassign', { assigneeId: nextAssigneeId });
        setDetail(updated);
      }
      await loadDetail(detail.id);
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err));
      await loadDetail(detail.id);
    } finally {
      setAssigning(false);
    }
  };

  const toggleTaskType = async (tagId: string): Promise<void> => {
    if (!detail || !canEditTaskType || savingTags) return;
    const nextTagIds = detail.tagIds.includes(tagId)
      ? detail.tagIds.filter((id) => id !== tagId)
      : [...detail.tagIds, tagId];
    setSavingTags(true);
    setMessage('');
    try {
      const updated = await updateTask(detail.id, { tagIds: [...nextTagIds] });
      setDetail(updated);
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setSavingTags(false);
    }
  };

  const submitComment = async (): Promise<void> => {
    if (!detail || !commentBody.trim() || posting) return;
    setPosting(true);
    setMessage('');
    try {
      await addTaskComment(detail.id, commentBody.trim());
      setCommentBody('');
      await loadDetail(detail.id);
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setPosting(false);
    }
  };

  const selectValue = detail?.assigneeId ?? '';

  const slackSourceLinks = useMemo(() => {
    const permalink = detail?.slackPermalink;
    if (!permalink) return null;
    return getSlackSourceLinks(
      permalink,
      typeof navigator !== 'undefined' ? navigator.userAgent : '',
    );
  }, [detail?.slackPermalink]);

  const copySlackLink = async (): Promise<void> => {
    if (!slackSourceLinks) return;
    try {
      await navigator.clipboard.writeText(slackSourceLinks.copyHref);
      setMessage('Slack link copied');
    } catch {
      setMessage('Could not copy link');
    }
  };

  return (
    <div className="task-detail-page">
      <TaskDetailNavbar showTitle={false} />

      {loading ? <p className="task-detail-status">Loading…</p> : null}
      {!loading && loadError ? <p className="task-detail-status">{loadError}</p> : null}
      {message ? <p className="task-message">{message}</p> : null}

      {detail ? (
        <div className="task-detail-body">
          <header className="task-detail-header">
            <h1 className="task-detail-title">{detail.title}</h1>
            <span className={`task-status-badge task-status-${detail.status}`}>
              {taskStatusLabel(detail.status)}
            </span>
            <label className="task-detail-assignee-label">
              Assigned to
              <select
                className="task-detail-assignee-select"
                value={selectValue}
                disabled={!assigneeSelectEnabled}
                aria-busy={assigning}
                onChange={(e) => void onAssigneeChange(e.target.value)}
              >
                <option value="">(Unassigned)</option>
                {assigneeOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            {canEditTaskType ? (
              <div className="task-detail-type">
                <span className="task-detail-assignee-label">Task type</span>
                <div
                  className="task-tag-filter task-detail-type-chips"
                  role="group"
                  aria-label="Task type"
                  aria-busy={savingTags}
                >
                  {activeTypeTags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      className={`task-tag-chip ${detail.tagIds.includes(tag.id) ? 'selected' : ''}`}
                      disabled={savingTags || assigning || actionBusy}
                      onClick={() => void toggleTaskType(tag.id)}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : detail.tagNames.length ? (
              <p className="task-detail-meta">Task type: {detail.tagNames.join(', ')}</p>
            ) : null}
          </header>
          {detail.description ? <p>{detail.description}</p> : null}
          {slackSourceLinks ? (
            <div className="task-slack-source">
              <a
                href={slackSourceLinks.primaryHref}
                {...(slackSourceLinks.primaryOpensNativeApp
                  ? {}
                  : { target: '_blank', rel: 'noreferrer' })}
              >
                {slackSourceLinks.primaryOpensNativeApp ? 'Open in Slack' : 'Slack source'}
              </a>
              <button type="button" onClick={() => void copySlackLink()}>
                Copy message link
              </button>
              {slackSourceLinks.showThreadHint ? (
                <p className="task-detail-meta">
                  If nothing opens, copy the link, paste it into a Slack message, and tap it.
                </p>
              ) : null}
            </div>
          ) : null}
          {detail.spec ? <TaskMarkdown source={detail.spec} /> : null}

          <div className="task-detail-actions">
            {visibleActions.map((t) => (
              <button
                key={t.action}
                type="button"
                disabled={actionBusy || assigning}
                onClick={() => void runAction(t.action)}
              >
                {t.action}
              </button>
            ))}
          </div>

          <div className="task-comments">
            <h3>Comments</h3>
            {detail.comments.map((c) => {
              const postedAt = formatDateTimeShort(c.createdAt);
              return (
                <div key={c.id} className="task-comment">
                  <div className="task-comment-header">
                    <strong>{c.authorName ?? 'User'}</strong>
                    {postedAt ? (
                      <time className="task-comment-time" dateTime={c.createdAt}>
                        {postedAt}
                      </time>
                    ) : null}
                  </div>
                  <p className="task-comment-body">{c.body}</p>
                </div>
              );
            })}
            <label>
              Add comment
              <textarea
                value={commentBody}
                disabled={posting}
                onChange={(e) => setCommentBody(e.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={posting || !commentBody.trim()}
              aria-busy={posting}
              onClick={() => void submitComment()}
            >
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TaskDetailPage;
